/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import msdyn365Commerce, * as Msdyn365 from '@msdyn365-commerce/core';
import axios from 'axios';
import { 
    generateErrorResponse, 
    generateSuccessResponse, 
    getEmarsysHeader 
} from './utilities/emarsys-utils';

const getLogger = (cittaInstrumentationKey: string): any => {
    if (process.env.CURRENT_ENVIRONMENT !== 'web' && cittaInstrumentationKey) {
        const appInsights = require('applicationinsights');
        return new appInsights.TelemetryClient(cittaInstrumentationKey);
    }
    return null
}

// tslint:disable-next-line
const EMARSYS_API_BASE_URL = 'https://api.emarsys.net';

/**
 * PostContactEvent Input Action
 */
export class PostContactEventInput implements Msdyn365.IActionInput {
    // TODO: Determine if the results of this get action should cache the results and if so provide
    // a cache object type and an appropriate cache key
    
    public getCacheKey = () => `PostEmarsysContactEventCacheKey`;
    public getCacheObjectType = () => 'PostEmarsysContactEventObjectType';
    public dataCacheType = (): Msdyn365.CacheType => 'request';
}
export interface IPostContactEventData {
    Result: string;
    Message: string;
    ErrorMessage: string;
}

/**
 * TODO: Use this function to create the input required to make the action call
 */
const createInput = (args: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    return new PostContactEventInput();
};

/**
 * TODO: Use this function to call your action and process the results as needed
 */
async function action(input: PostContactEventInput, ctx: Msdyn365.IActionContext): Promise<IPostContactEventData> {
    let telemetryClient = getLogger(ctx.requestContext.app.config.cittaInstrumentationKey);

    const logMessage = (message: string) => {
        if (telemetryClient) {
          telemetryClient.trackTrace({ message: message });
        } else {
          console.log(message);
        }
    }

    const postBody = ctx.requestContext.postBody;
    const contentObject = postBody?.content;

    const bodyObject = JSON.parse(contentObject!.toString());
    logMessage(`Submit to emarsys page with payload: ${JSON.stringify(bodyObject)}`);

    if (ctx.requestContext.query?.failServerSide === 'true') {
        if (!msdyn365Commerce.isBrowser) {
            throw new Error('Thrown action');
        } else {
            logMessage('Post emarsys contact: server side fail 1');
            return generateErrorResponse('Post Emarsys Contact: server side fail 1');
        }
    } else if (ctx.requestContext.query?.returnMockData === 'true') {
        logMessage('Post emarsys contact: server side fail 2');
        return generateErrorResponse('Post Emarsys Contact: server side fail 2');
    }

   try {
        const usernameKeyvaultName = 'EmarsysAPIUsernameNZv2';
        const secretKeyvaultName = 'EmarsysAPISecretNZv2';
        const user = await msdyn365Commerce.secretManager?.getSecretValue(usernameKeyvaultName, ctx.requestContext.apiSettings.baseUrl);
        const secret = await msdyn365Commerce.secretManager?.getSecretValue(secretKeyvaultName, ctx.requestContext.apiSettings.baseUrl);

        if (!user?.value || !secret?.value) {
            const errors = {
                usernameError: user?.error?.message,
                secretError: user?.error?.message
            };       
            logMessage(`Post emarsys contact: unable to get keyvault values ${JSON.stringify(errors)}`);
            return generateErrorResponse(`Post Emarsys Contact: unable to get keyvault values ${JSON.stringify(errors)}`);
        }

        const headers = getEmarsysHeader(user?.value || '', secret?.value || '');

        let emarsysPostBody = {
            key_id: '3',
            // tslint:disable-next-line
            3: bodyObject?.emailAddress,
            1: bodyObject?.firstName, 
            2: bodyObject?.lastName
        };

        if (bodyObject?.businessName) {
            emarsysPostBody['18'] = bodyObject?.businessName;
        }

        if (bodyObject?.industryPosition) {
            emarsysPostBody[bodyObject?.industryPositionFieldId] = bodyObject?.industryPosition;
        }
        
        emarsysPostBody[bodyObject?.emarsysFieldId] = '1';
        
        logMessage(`Post to Emarsys payload: ${JSON.stringify(emarsysPostBody)}`);

        return await axios.put(`${EMARSYS_API_BASE_URL}/api/v2/contact/?create_if_not_exists=1`,
            emarsysPostBody,
            {
                headers: headers
            })
            .then((response) => {
                logMessage(`Success message: ${JSON.stringify(response.data)}`);
                return generateSuccessResponse(`Success message: ${JSON.stringify(response.data)}`);
                
            })
            .catch((error) => {
                const errToReturn = error && error.response ? error.response.data : error
                logMessage(`Post Emarsys Contact: catch at post ${JSON.stringify(emarsysPostBody)} ERROR: ${JSON.stringify(errToReturn)}`);
                return generateErrorResponse(`Post Emarsys Contact: catch at post ${JSON.stringify(emarsysPostBody)} ERROR: ${JSON.stringify(errToReturn)}`);
            });

    } catch (e) {
        logMessage(`Post Emarsys Contact: catch ${JSON.stringify(e)} body: ${bodyObject}`);
        return generateErrorResponse(`Post Emarsys Contact: catch ${JSON.stringify(e)}`);
    }
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IPostContactEventData>>action,
    id: 'PostEmarsysContactEvent',
    input: createInput
});
